import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtlasStore } from '../../../../store/atlasStore';
import { TagFacetDimension, SparkLibrarySearchResponse, SparkLibraryItemState } from '../../types/sparkTypes';
import { SearchToolbar } from './SearchToolbar';
import { Results, DelayedLoader } from './Results';
import styles from '../../styles/InboxView.module.css';
import { JobDetailModal } from '../modals/JobDetailModal';
import { ConfirmationModal } from '../modals/ConfirmationModal';
import boardStyles from '../../styles/CreativeBoard.module.css';
import { atlasClient } from '../../../../api/atlasClient';
import { toast } from 'react-toastify';
import { ImportDropdown } from '../import';
import ActiveJobCard from '../ActiveJobCard';
import { useAuth } from '../../../../contexts/AuthContext';

interface EditorRequest {
  itemFqn: string;
  onClose: () => Promise<void>;
  onDataRefreshed: () => Promise<void>;
}

interface MyLibraryItemsTabProps {
  creativeTypeFqn: string;
  creativeTypeName: string;
  creativeTypePluralName: string;
  onEditorChange?: (editor: EditorRequest | null) => void;
}

export const MyLibraryItemsTab: React.FC<MyLibraryItemsTabProps> = ({
  creativeTypeFqn,
  creativeTypeName,
  creativeTypePluralName,
  onEditorChange,
}) => {
  const searchLibrary = useAtlasStore(s => s.searchLibraryItems);
  const fetchFacets = useAtlasStore(s => s.fetchLibraryFacets);
  const currentJobs = useAtlasStore(s => s.currentSparkJobs);
  const markSparkJobAsSeen = useAtlasStore(s => s.markSparkJobAsSeen);
  const deleteLibraryItem = useAtlasStore(s => s.deleteLibraryItem);
  const { user } = useAuth();

  const [q, setQ] = useState('');
  const [sortField, setSortField] = useState<'name' | 'publishedAt'>('publishedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const viewMode = 'list' as const;
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [facets, setFacets] = useState<TagFacetDimension[]>([]);
  const [selectedTagsByDim, setSelectedTagsByDim] = useState<Record<string, string[]>>({});
  const [results, setResults] = useState<SparkLibrarySearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewItemFqn, setPreviewItemFqn] = useState<string | null>(null);
  const [createdItemFqn, setCreatedItemFqn] = useState<string | null>(null);

  // Selection / delete state
  const [selectedItemFqns, setSelectedItemFqns] = useState<string[]>([]);
  const [selectedItemStates, setSelectedItemStates] = useState<Record<string, SparkLibraryItemState>>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState<string[]>([]);

  // PDF job tracking
  const [pendingPdfCompletionCount, setPendingPdfCompletionCount] = useState(0);
  const [pendingPdfCompletionFqns, setPendingPdfCompletionFqns] = useState<string[]>([]);
  const [isPdfCompletionModalOpen, setIsPdfCompletionModalOpen] = useState(false);
  const pdfCompletionInitRef = useRef(false);
  const pdfCompletionSeenRef = useRef<Set<string>>(new Set());
  const pdfPrevStatusRef = useRef<Map<string, string>>(new Map());
  const pdfCompletionViewStartRef = useRef<number>(Date.now());

  // Job detail modal
  const [selectedJobFqnForDetail, setSelectedJobFqnForDetail] = useState<string | null>(null);

  const hasPublishedSelected = useMemo(() => {
    return selectedItemFqns.some((fqn) => selectedItemStates[fqn] === 'published');
  }, [selectedItemFqns, selectedItemStates]);

  const activePdfJobs = useMemo(() => {
    const jobs = currentJobs || [];
    return jobs.filter(job => {
      const isActive = job._status === 'pending' || job._status === 'running';
      const isPdfImport = job._job_type === 'spark_pdf_import';
      if (!isActive || !isPdfImport) return false;
      return job._source_type_fqn === creativeTypeFqn || job._target_type_fqn === creativeTypeFqn;
    });
  }, [currentJobs, creativeTypeFqn]);

  const tagsParam = useMemo(() => {
    const entries: string[] = [];
    Object.entries(selectedTagsByDim).forEach(([dim, elems]) => {
      elems.forEach(e => entries.push(`${dim}:${e}`));
    });
    return entries;
  }, [selectedTagsByDim]);

  // Use refs so search/facet callbacks always read the latest values
  const tagsParamRef = useRef(tagsParam);
  tagsParamRef.current = tagsParam;

  // Track latest requests to avoid stale updates
  const latestSearchIdRef = useRef(0);
  const latestFacetsIdRef = useRef(0);

  const runSearch = useCallback(async (pageArg?: number) => {
    const requestId = ++latestSearchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const effectivePage = pageArg ?? page;
      const params: any = {
        q,
        tags: tagsParamRef.current,
        page: effectivePage,
        page_size: pageSize,
        sort: sortField,
        dir: sortDir,
        include_drafts: true,
        owner: 'me',
        creative_type_fqn: creativeTypeFqn,
      };
      const data = await searchLibrary(params);
      if (requestId === latestSearchIdRef.current) {
        setResults(data);
      }
    } catch (e: any) {
      if (requestId === latestSearchIdRef.current) {
        setError(e?.message || 'Search failed');
      }
    } finally {
      if (requestId === latestSearchIdRef.current) {
        setLoading(false);
      }
    }
  }, [q, page, pageSize, sortField, sortDir, searchLibrary, creativeTypeFqn]);

  const loadFacets = useCallback(async () => {
    const requestId = ++latestFacetsIdRef.current;
    try {
      const params: any = {
        q,
        tags: tagsParamRef.current,
        include_drafts: true,
        owner: 'me',
        creative_type_fqn: creativeTypeFqn,
      };
      const { dimensions } = await fetchFacets(params);
      if (requestId === latestFacetsIdRef.current) {
        setFacets(dimensions || []);
      }
    } catch {
      // Non-fatal; keep previous facets
    }
  }, [fetchFacets, q, creativeTypeFqn]);

  // Initial load
  useEffect(() => {
    loadFacets();
    runSearch(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run search when sort changes
  useEffect(() => {
    runSearch(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDir]);

  // Re-run when tag selection changes
  useEffect(() => {
    const firstPage = 1;
    setPage(firstPage);
    setSelectedItemFqns([]);
    setSelectedItemStates({});
    loadFacets();
    runSearch(firstPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsParam]);

  // PDF completion tracking
  useEffect(() => {
    if (!user?.id) return;
    const jobs = currentJobs || [];
    const pdfJobs = jobs.filter(
      job => job._job_type === 'spark_pdf_import' && job._user_fqn === user.id
    );

    const isInitializing = !pdfCompletionInitRef.current;
    if (isInitializing) {
      pdfCompletionInitRef.current = true;
    }

    let newCompletions = 0;
    const completedFqns: string[] = [];
    const nextMap = new Map<string, string>();
    pdfJobs.forEach(job => {
      const status = job._status || '';
      nextMap.set(job.fqn, status);
      const prevStatus = pdfPrevStatusRef.current.get(job.fqn);
      const completedAt = new Date(job._completed_at || job._created_at || '').getTime();
      const completedAfterViewStart = Number.isFinite(completedAt)
        ? completedAt > pdfCompletionViewStartRef.current
        : true;
      if (
        status === 'completed' &&
        (isInitializing || prevStatus !== 'completed') &&
        !pdfCompletionSeenRef.current.has(job.fqn) &&
        !job._completion_seen_by_current_user &&
        completedAfterViewStart
      ) {
        pdfCompletionSeenRef.current.add(job.fqn);
        newCompletions += 1;
        completedFqns.push(job.fqn);
      }
    });
    pdfPrevStatusRef.current = nextMap;

    if (newCompletions > 0) {
      setPendingPdfCompletionCount(count => count + newCompletions);
      setPendingPdfCompletionFqns(prev => {
        const next = new Set(prev);
        completedFqns.forEach(fqn => next.add(fqn));
        return Array.from(next);
      });
      setIsPdfCompletionModalOpen(true);
    }
  }, [currentJobs, user?.id]);

  const onToggleTag = (dimensionFqn: string, elementFqn: string) => {
    setSelectedTagsByDim(prev => {
      const current = new Set(prev[dimensionFqn] || []);
      if (current.has(elementFqn)) current.delete(elementFqn); else current.add(elementFqn);
      return { ...prev, [dimensionFqn]: Array.from(current) };
    });
  };

  const onSearchClick = async () => {
    const firstPage = 1;
    setPage(firstPage);
    setSelectedItemFqns([]);
    setSelectedItemStates({});
    await loadFacets();
    await runSearch(firstPage);
  };

  const onToggleSort = () => setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));

  const totalPages = useMemo(() => results ? Math.max(1, Math.ceil(results.total / results.page_size)) : 1, [results]);
  const goToPage = async (p: number) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
    await runSearch(next);
  };

  const handlePdfCompletionRefresh = async () => {
    const firstPage = 1;
    setPendingPdfCompletionCount(0);
    setIsPdfCompletionModalOpen(false);
    setPage(firstPage);
    setSelectedItemFqns([]);
    setSelectedItemStates({});

    if (pendingPdfCompletionFqns.length > 0) {
      await markSparkJobAsSeen(pendingPdfCompletionFqns);
      setPendingPdfCompletionFqns([]);
    }

    // Always sort by newest first and refresh
    setSortField('publishedAt');
    setSortDir('desc');
    await loadFacets();
    await runSearch(firstPage);
  };

  const handlePdfCompletionDismiss = async () => {
    setPendingPdfCompletionCount(0);
    setIsPdfCompletionModalOpen(false);
    if (pendingPdfCompletionFqns.length > 0) {
      await markSparkJobAsSeen(pendingPdfCompletionFqns);
      setPendingPdfCompletionFqns([]);
    }
  };

  const handleDeleteSelected = () => {
    if (!selectedItemFqns.length) return;
    setItemsToDelete(selectedItemFqns);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemsToDelete.length) return;
    setIsDeleting(true);
    let successCount = 0;
    let failureCount = 0;
    await Promise.all(itemsToDelete.map(async (fqn) => {
      try {
        await deleteLibraryItem(fqn);
        successCount += 1;
      } catch {
        failureCount += 1;
      }
    }));
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} item${successCount === 1 ? '' : 's'}`);
    }
    if (failureCount > 0) {
      toast.error(`Failed to delete ${failureCount} item${failureCount === 1 ? '' : 's'}`);
    }
    setItemsToDelete([]);
    setSelectedItemFqns([]);
    setSelectedItemStates({});
    setIsDeleteModalOpen(false);
    setIsDeleting(false);
    await loadFacets();
    await runSearch(page);
  };

  // Refs for stable closures in editor callbacks
  const createdItemFqnRef = useRef(createdItemFqn);
  createdItemFqnRef.current = createdItemFqn;
  const pageRef = useRef(page);
  pageRef.current = page;
  const loadFacetsRef = useRef(loadFacets);
  loadFacetsRef.current = loadFacets;
  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  // Notify parent when editor should open/close (parent renders editor at top level)
  useEffect(() => {
    if (previewItemFqn) {
      onEditorChange?.({
        itemFqn: previewItemFqn,
        onClose: async () => {
          if (createdItemFqnRef.current) {
            const firstPage = 1;
            setPage(firstPage);
            await loadFacetsRef.current();
            await runSearchRef.current(firstPage);
            setCreatedItemFqn(null);
          }
          setPreviewItemFqn(null);
        },
        onDataRefreshed: async () => { await runSearchRef.current(pageRef.current); },
      });
    } else {
      onEditorChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewItemFqn]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, padding: '24px var(--spacing-lg, 1.5rem) var(--spacing-lg, 1.5rem)' }}>
      <SearchToolbar
        q={q}
        onChangeQ={setQ}
        facets={facets}
        selected={selectedTagsByDim}
        onToggleTag={onToggleTag}
        sortField={sortField}
        sortDir={sortDir}
        onChangeSort={(field, dir) => {
          setSortField(field);
          setSortDir(dir);
        }}
        onToggleSort={onToggleSort}
        onSearch={onSearchClick}
        loading={loading}
        onClearAll={() => {
          setSelectedTagsByDim({});
        }}
        onClearSelection={() => {
          setSelectedItemFqns([]);
          setSelectedItemStates({});
        }}
        rightActions={
          <>
            <ImportDropdown
              creativeTypeFqn={creativeTypeFqn}
              creativeTypeName={creativeTypePluralName}
              disabled={false}
              buttonClassName={styles.libraryImportButton}
              onImportComplete={async () => {
                const firstPage = 1;
                setPage(firstPage);
                await loadFacets();
                await runSearch(firstPage);
              }}
            />
            <button
              className={styles.libraryNewButton}
              onClick={async () => {
                try {
                  const created = await atlasClient.createLibraryItem({ creative_type_fqn: creativeTypeFqn });
                  setCreatedItemFqn(created.fqn);
                  setPreviewItemFqn(created.fqn);
                } catch (e: any) {
                  const message = e?.response?.data?.error || (e instanceof Error ? e.message : 'Failed to create item');
                  toast.error(message);
                }
              }}
              title="Create new personal item"
            >
              + New {creativeTypeName}
            </button>
            {(() => {
              const disabled = selectedItemFqns.length === 0 || hasPublishedSelected;
              const tooltip = hasPublishedSelected
                ? 'Cannot delete published items'
                : 'Delete selected items';
              return (
                <button
                  className={styles.libraryDeleteButton}
                  onClick={() => !disabled && handleDeleteSelected()}
                  title={tooltip}
                  disabled={disabled}
                >
                  Delete
                </button>
              );
            })()}
          </>
        }
      />

      {/* Active PDF Import Jobs */}
      {activePdfJobs.length > 0 && (
        <div className={boardStyles.activeJobsSection}>
          <ul className={boardStyles.activeJobsList}>
            {activePdfJobs.map(job => (
              <li key={job.fqn} className={boardStyles.activeJobListItem}>
                <ActiveJobCard
                  job={job}
                  onClick={(j) => setSelectedJobFqnForDetail(j.fqn)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Scrollable results area */}
      <section style={{ marginTop: 12, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <DelayedLoader visible={loading} />
        {error && <div style={{ color: 'red' }}>{error}</div>}
        {!loading && results && (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Results
                items={results.items}
                viewMode={viewMode}
                onOpen={(fqn) => setPreviewItemFqn(fqn)}
                selectable={true}
                showPublishedBadge={true}
                hasActiveFilters={q.trim().length > 0 || tagsParam.length > 0}
                selectedFqns={selectedItemFqns}
                onToggleSelect={(fqn, checked) => {
                  const hit = results?.items.find(item => item.fqn === fqn);
                  setSelectedItemFqns(prev => {
                    if (checked) {
                      if (prev.includes(fqn)) return prev;
                      return [...prev, fqn];
                    }
                    return prev.filter(x => x !== fqn);
                  });
                  setSelectedItemStates(prev => {
                    if (checked && hit) {
                      return { ...prev, [fqn]: hit.state };
                    }
                    if (!checked && prev[fqn]) {
                      const next = { ...prev };
                      delete next[fqn];
                      return next;
                    }
                    return prev;
                  });
                }}
              />
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                {(() => {
                  const pages: (number | string)[] = [];
                  const cur = page;
                  const total = totalPages;
                  const start = Math.max(1, cur - 2);
                  const end = Math.min(total, cur + 2);
                  if (start > 1) pages.push(1);
                  if (start > 2) pages.push('…');
                  for (let p = start; p <= end; p++) pages.push(p);
                  if (end < total - 1) pages.push('…');
                  if (end < total) pages.push(total);
                  return (
                    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <button
                        disabled={cur <= 1}
                        onClick={() => goToPage(cur - 1)}
                        style={{ border: 'none', background: 'transparent', cursor: cur <= 1 ? 'default' : 'pointer' }}
                        aria-label="Previous page"
                      >
                        {'<'}
                      </button>
                      {pages.map((item, idx) =>
                        typeof item === 'number' ? (
                          <button
                            key={`p-${item}-${idx}`}
                            onClick={() => goToPage(item)}
                            aria-current={item === cur ? 'page' : undefined}
                            aria-label={`Go to page ${item}`}
                            style={{
                              minWidth: 32,
                              padding: '4px 8px',
                              borderRadius: 6,
                              border: item === cur ? '1px solid #2E90FA' : '1px solid #D0D5DD',
                              background: item === cur ? '#E6F0FE' : 'white',
                              color: item === cur ? '#1D4ED8' : '#111827',
                              cursor: 'pointer',
                            }}
                          >
                            {item}
                          </button>
                        ) : (
                          <span key={`e-${idx}`} style={{ padding: '0 4px', color: '#6B7280' }}>…</span>
                        )
                      )}
                      <button
                        disabled={cur >= total}
                        onClick={() => goToPage(cur + 1)}
                        style={{ border: 'none', background: 'transparent', cursor: cur >= total ? 'default' : 'pointer' }}
                        aria-label="Next page"
                      >
                        {'>'}
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </section>

      <JobDetailModal
        isOpen={Boolean(selectedJobFqnForDetail)}
        onClose={() => setSelectedJobFqnForDetail(null)}
        jobFqn={selectedJobFqnForDetail}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the selected item(s)?\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isConfirming={isDeleting}
        variant="destructive"
      />
      <ConfirmationModal
        isOpen={isPdfCompletionModalOpen}
        onClose={handlePdfCompletionDismiss}
        onConfirm={handlePdfCompletionRefresh}
        title="PDF import complete"
        message={
          pendingPdfCompletionCount === 1
            ? "Your PDF import has finished.\nRefresh to view the new items?"
            : `${pendingPdfCompletionCount} PDF imports have finished.\nRefresh to view the new items?`
        }
        confirmText="Refresh view"
        cancelText="Later"
      />
    </div>
  );
};
